import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Library, Users, BookMarked } from "lucide-react";
import SiteNavbar from "../components/SiteNavbar";
import SiteFooter from "../components/SiteFooter";
import HowItWorksSteps from "../components/HowItWorksSteps";

const ease = [0.21, 0.47, 0.32, 0.98];

const highlights = [
  {
    icon: Library,
    title: "Your shelf, your pace",
    desc: "Track what you own, want, and have finished — without gamified pressure.",
  },
  {
    icon: Users,
    title: "Small circles",
    desc: "Reader groups built for conversation, not endless notifications.",
  },
  {
    icon: BookMarked,
    title: "Shared discovery",
    desc: "Browse curated shelves and see what real readers recommend.",
  },
];

const HowItWorksPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      data-testid="how-it-works-page"
      className="min-h-screen paper-overlay"
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      <SiteNavbar />

      <main className="pt-32 md:pt-40 pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease }}
            className="text-center max-w-3xl mx-auto"
          >
            <span
              className="inline-block text-[11px] uppercase tracking-[0.25em] font-semibold mb-4"
              style={{ color: "var(--brand)" }}
            >
              How it works
            </span>
            <h1
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] mb-6"
              style={{ color: "var(--text)" }}
            >
              Three quiet steps to a{" "}
              <span className="italic" style={{ color: "var(--brand)" }}>
                richer reading life
              </span>
            </h1>
            <p
              className="text-lg md:text-xl leading-relaxed"
              style={{ color: "var(--text-soft)" }}
            >
              PagePeers is a calm home for your books — organize your shelf, join
              thoughtful reader circles, and discover stories through people you
              trust.
            </p>
          </motion.div>

          <HowItWorksSteps className="mt-20 md:mt-24" />

          <div className="mt-24 md:mt-32 grid md:grid-cols-3 gap-6">
            {highlights.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, ease, delay: i * 0.08 }}
                className="rounded-3xl p-8 cozy-card"
                style={{ background: "var(--surface)" }}
              >
                <item.icon
                  className="w-6 h-6 mb-5"
                  style={{ color: "var(--brand)" }}
                />
                <h2
                  className="font-serif text-xl font-medium mb-2"
                  style={{ color: "var(--text)" }}
                >
                  {item.title}
                </h2>
                <p className="text-sm leading-relaxed" style={{ color: "var(--text-soft)" }}>
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
            className="mt-24 md:mt-32 rounded-3xl p-10 md:p-14 text-center"
            style={{
              background:
                "linear-gradient(135deg, var(--brand-tint) 0%, var(--bg-alt) 100%)",
              border: "1px solid var(--border)",
            }}
          >
            <h2
              className="font-serif text-3xl md:text-4xl font-medium mb-4"
              style={{ color: "var(--text)" }}
            >
              Ready to start your shelf?
            </h2>
            <p
              className="text-base mb-8 max-w-lg mx-auto"
              style={{ color: "var(--text-soft)" }}
            >
              Join PagePeers free and build a reading life that feels unhurried,
              social, and entirely yours.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://club.pagepeers.com/register"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-transform hover:scale-[1.02]"
                style={{
                  background: "var(--brand)",
                  color: "var(--on-brand)",
                  boxShadow: "0 6px 20px -6px rgba(194,90,55,0.5)",
                }}
              >
                Join the Club
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/shelves"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm border transition-colors"
                style={{
                  borderColor: "var(--border)",
                  color: "var(--text)",
                  background: "var(--surface)",
                }}
              >
                Explore shelves
              </Link>
            </div>
          </motion.div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default HowItWorksPage;
